import { useContext, useState } from "react"
import { Chip } from "@heroui/react"
import { CircleStackIcon } from "@heroicons/react/24/outline"
import { useLazyQuery, useQuery } from "@apollo/client"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  IntegrationTypeBadge, 
  MisBadge, 
  MisCountBadge, 
  Page, 
  PageHeader,
  SiteDataMenu,
  SubjectLessonsList 
} from "@components"

import { ContextualSidebarContext, UserContext } from "@context"

import { 
  GET_SUBJECTS, 
  GET_SITE_DATA_SUBJECTS_SUMMARY, 
  GET_SITE_DATA_SUBJECTS_XLS_DOWNLOAD_LINK 
} from "@graphql"

import { 
  downloadFile, 
  lastImportDuration,
  showLoader,
} from "@helpers"

import {   
  useColumnManager,
  useSetActiveSidebarLink 
} from "@hooks"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { Stats } from "./Stats"

const Subjects = () => {
  const { user } = useContext(UserContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [isDownloading, setIsDownloading] = useState(false)
  const [refetching, setRefetching] = useState(false)
  const [selectedFilters, setSelectedFilters] = useState(Object.keys(filters))
  const [searchTerm, setSearchTerm] = useState("")
  const [sortDescriptor, setSortDescriptor] = useState({ column: "name", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  useSetActiveSidebarLink("SITE_DATA_SUBJECTS", "SITE_DATA")
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "data.subjects.columns", defaultColumns)

  const summaryData = useQuery(GET_SITE_DATA_SUBJECTS_SUMMARY, {
    variables: {
      clientId: user.me.client.id,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const { loading, error, data, refetch, networkStatus } = useQuery(GET_SUBJECTS, {
    variables: {
      searchTerm,
      page,
      perPage,
      clientId: user.me.client.id,
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],
      where: selectedFilters.length < 2 ? { column: "IS_SYNC", value: selectedFilters.includes("sync") } : null,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const [getSiteDataSubjectsXlsDownloadLink] = useLazyQuery(GET_SITE_DATA_SUBJECTS_XLS_DOWNLOAD_LINK, {
    variables: { clientId: user.me.client.id },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.dataSubjectsXlsDownload.filename, data.dataSubjectsXlsDownload.url)
      setIsDownloading(false)
    },
  })

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    refetch().then(() => setRefetching(false))
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getSiteDataSubjectsXlsDownloadLink()
    }
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowLessons = subject =>
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: subject.name,
      subTitle: `${subject.name} lessons are listed below.`,
      content: <SubjectLessonsList subject={subject} />,
      size: "lg",
    }))

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilters => {
    setSelectedFilters(Object.keys(filters).filter(filter => selectedFilters.has(filter)))
  }

  const renderCell = (subject, column) => {
    if (column === "actions") {
      const items = [{ id: 1, handleClick: () => handleShowLessons(subject), caption: "View Lessons" }]

      return <EllipsisDropdown items={items} />
    }

    if (column === "is_sync" && subject.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync", content: "text-white" }} size="sm" variant="flat">
          Sync
        </Chip>
      )
    }

    if (column === "is_sync" && !subject.is_sync) {
      return (
        <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white" }} size="sm" variant="flat">
          Custom
        </Chip>
      )
    }

    return subject[column]
  }

  if (error) return `Error! ${error.message}`

  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.client?.subjects?.data, refetching);

  return (
    <Page>
      <PageHeader
        title="Subject Data"
        subTitle="Imported subject data from your MIS / CSV file."
        icon={<CircleStackIcon className="page-header-icon" />}
        contentBottom={
          <div className="flex flex-wrap gap-2">
            <IntegrationTypeBadge name={user?.me?.client?.importType?.name || "unknown"} />
            <MisBadge name={user?.me?.client?.mis || "n/a"} />
            <MisCountBadge name={`${summaryData?.data?.dataSubjectsSummary?.total || 0} Subjects`} />
          </div>
        }
        actions={<SiteDataMenu clientId={user.me.client.id} />}
      />

      <Stats
        summary={{
          dataAge: summaryData?.data?.dataSubjectsSummary?.data_age,
          lastImport: lastImportDuration(summaryData?.data?.dataSubjectsSummary?.imported_at),
          total: summaryData?.data?.dataSubjectsSummary?.total,
          syncCount: summaryData?.data?.dataSubjectsSummary?.sync_count,
          customCount: summaryData?.data?.dataSubjectsSummary?.custom_count,
          missingLessonCount: summaryData?.data?.dataSubjectsSummary?.missing_lesson_count,
        }}
      />

      <DataTable
        columns={columns}
        currentPage={page}
        currentSortOrder={sortDescriptor}
        data={data?.client?.subjects?.data}
        downloadOptions={downloadOptions}
        filters={filters}
        handleDownload={type => handleDownload(type)}
        handlePageChange={page => setPage(page)}
        handleRefetch={() => handleRefetchButtonClicked()}
        handleRenderCell={(item, key) => renderCell(item, key)}
        handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
        handleSearchClear={handleSearchClear}
        handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
        handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
        handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
        isDownloading={isDownloading}
        loading={showLoadingPlaceholder}
        isRefetching={refetching}
        noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Subjects Found" description="No Subjects found! Try clearing any filters." />}
        paginatorInfo={data?.client?.subjects?.paginatorInfo}
        searchInputPlaceHolder="Search by name"
        searchTerm={searchTerm}
        selectedColumns={selectedColumns}
        selectedFilters={selectedFilters}
        showDownloadButton={true}
        showRefetchButton={true}
      />
    </Page>
  )
}

export default Subjects
